import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, num, mul, variable, canonicalise } from './expr';

export interface Equation {
  lhs: Expr;
  rhs: Expr;
}

export type SystemSolution =
  | { kind: 'unique'; solutions: ReadonlyMap<string, Expr> }
  | { kind: 'inconsistent' }
  | { kind: 'dependent'; parameterisation: ReadonlyMap<string, Expr> };

export type SystemStep =
  | { rule: 'substitute'; from: string; into: number; result: Equation; description: string }
  | { rule: 'row-swap'; a: number; b: number; description: string }
  | {
      rule: 'row-scale';
      row: number;
      factor: Fraction;
      resultRow: readonly Fraction[];
      description: string;
    }
  | {
      rule: 'row-add';
      sourceRow: number;
      targetRow: number;
      factor: Fraction;
      resultRow: readonly Fraction[];
      description: string;
    };

export interface SystemResult {
  solution: SystemSolution;
  method: 'substitution' | 'gaussian';
  steps: SystemStep[];
}

/**
 * Extract linear coefficients from an expression for a given set of variables.
 * Returns { coeffs, constant } or null if the expression is not linear.
 *
 * For each variable in vars, extract its coefficient (coefficient of var1 * 1).
 * Reject anything nonlinear (powers, nested muls, functions, etc.).
 */
function linearCoefficients(
  expr: Expr,
  vars: readonly string[],
): { coeffs: Fraction[]; constant: Fraction } | null {
  const coeffMap = new Map<string, Fraction>();
  let constant = Fraction.ZERO;

  /**
   * Recursively collect linear terms.
   * Returns the net contribution to constant; updates coeffMap for variables.
   */
  function collect(e: Expr): Fraction | null {
    switch (e.kind) {
      case 'num': {
        return e.value;
      }

      case 'var': {
        if (!vars.includes(e.name)) return null;
        coeffMap.set(e.name, (coeffMap.get(e.name) ?? Fraction.ZERO).add(Fraction.ONE));
        return Fraction.ZERO;
      }

      case 'add': {
        let sum = Fraction.ZERO;
        for (const child of e.children) {
          const contrib = collect(child);
          if (contrib === null) return null;
          sum = sum.add(contrib);
        }
        return sum;
      }

      case 'mul': {
        // Exactly two children: one numeric, one variable (or both numeric).
        if (e.children.length === 0) {
          return Fraction.ONE;
        }
        if (e.children.length === 1) {
          return collect(e.children[0]!);
        }
        if (e.children.length === 2) {
          const child0 = e.children[0]!;
          const child1 = e.children[1]!;

          // Case 1: both are numbers -> contribute to constant
          if (child0.kind === 'num' && child1.kind === 'num') {
            return child0.value.mul(child1.value);
          }

          // Case 2: one is a number, one is a variable
          if (child0.kind === 'num' && child1.kind === 'var') {
            if (!vars.includes(child1.name)) return null;
            coeffMap.set(
              child1.name,
              (coeffMap.get(child1.name) ?? Fraction.ZERO).add(child0.value),
            );
            return Fraction.ZERO;
          }

          if (child0.kind === 'var' && child1.kind === 'num') {
            if (!vars.includes(child0.name)) return null;
            coeffMap.set(
              child0.name,
              (coeffMap.get(child0.name) ?? Fraction.ZERO).add(child1.value),
            );
            return Fraction.ZERO;
          }

          // Case 3: mul of two variables or other non-linear -> reject
          return null;
        }

        // More than 2 children -> non-linear
        return null;
      }

      default: {
        // pow, func, sigma -> non-linear
        return null;
      }
    }
  }

  const constContrib = collect(expr);
  if (constContrib === null) return null;
  constant = constContrib;

  // Build coefficient array in var order
  const coeffs: Fraction[] = Array.from(
    { length: vars.length },
    (_, i) => coeffMap.get(vars[i]!) ?? Fraction.ZERO,
  );

  return { coeffs, constant };
}

/**
 * Extract an augmented matrix row from an equation lhs = rhs.
 * Returns [a_0, a_1, ..., a_n | b] where lhs - rhs = a_0 * var0 + a_1 * var1 + ... + b.
 */
function equationToMatrixRow(eq: Equation, vars: readonly string[]): Fraction[] | null {
  // lhs - rhs
  const diff: Expr = {
    kind: 'add',
    children: [
      eq.lhs,
      {
        kind: 'mul',
        children: [num(-1), eq.rhs],
      },
    ],
  };

  const result = linearCoefficients(diff, vars);
  if (result === null) return null;

  // Return [coeffs..., constant (negated to move to RHS)]
  return [...result.coeffs, result.constant.neg()];
}

/**
 * Gaussian elimination to RREF, returning the RREF matrix and list of pivot columns.
 */
function gaussianRREF(
  matrix: Fraction[][],
  steps: SystemStep[],
  equationCount: number,
  varCount: number,
): { rref: Fraction[][]; pivots: (number | null)[] } {
  const rref = matrix.map((row) => Array.from(row));
  const pivots: (number | null)[] = Array.from({ length: equationCount }, () => null);
  // perm[physicalRow] = the original equation index currently occupying that physical row.
  // Row swaps permute physical rows in place, so step indices are emitted through perm to
  // keep them stable original-equation identities rather than post-swap physical positions.
  const perm = Array.from({ length: equationCount }, (_, i) => i);

  let currentRow = 0;

  for (let col = 0; col < varCount && currentRow < equationCount; col += 1) {
    // Find pivot in column col from row currentRow downward
    let pivotRow = -1;
    for (let row = currentRow; row < equationCount; row += 1) {
      if (rref[row]![col]!.num !== 0) {
        pivotRow = row;
        break;
      }
    }

    if (pivotRow === -1) continue; // No pivot in this column

    // Swap if needed
    if (pivotRow !== currentRow) {
      const temp = rref[currentRow]!;
      rref[currentRow] = rref[pivotRow]!;
      rref[pivotRow] = temp;
      // Capture the pre-swap identities of the two positions being exchanged before mutating
      // perm, so the step reports which original equations swapped (not physical positions).
      const a = perm[currentRow]!;
      const b = perm[pivotRow]!;
      perm[currentRow] = b;
      perm[pivotRow] = a;
      steps.push({
        rule: 'row-swap',
        a,
        b,
        description: `Swap row ${currentRow} and row ${pivotRow}`,
      });
    }

    // Scale to make pivot 1
    const pivot = rref[currentRow]![col]!;
    if (!pivot.equals(Fraction.ONE)) {
      const factor = pivot.reciprocal();
      rref[currentRow] = rref[currentRow]!.map((c) => c.mul(factor));
      steps.push({
        rule: 'row-scale',
        row: perm[currentRow]!,
        factor,
        resultRow: Array.from(rref[currentRow]!),
        description: `Scale row ${currentRow} by ${factor.toString()}`,
      });
    }

    // Eliminate column col in all other rows
    for (let row = 0; row < equationCount; row += 1) {
      if (row === currentRow) continue;

      const factor = rref[row]![col]!;
      if (factor.num === 0) continue;

      // row := row - factor * currentRow
      for (let c = 0; c < varCount + 1; c += 1) {
        rref[row]![c] = rref[row]![c]!.sub(factor.mul(rref[currentRow]![c]!));
      }

      steps.push({
        rule: 'row-add',
        sourceRow: perm[currentRow]!,
        targetRow: perm[row]!,
        factor: factor.neg(),
        resultRow: Array.from(rref[row]!),
        description: `Add ${factor.neg().toString()} times row ${currentRow} to row ${row}`,
      });
    }

    pivots[currentRow] = col;
    currentRow += 1;
  }

  return { rref, pivots };
}

/**
 * Classify RREF and extract solution.
 */
function classifyRREF(
  rref: Fraction[][],
  pivots: (number | null)[],
  vars: readonly string[],
): SystemSolution {
  const equationCount = rref.length;
  const varCount = vars.length;

  // Check for inconsistency: a row [0 0 ... 0 | c] with c != 0
  for (let row = 0; row < equationCount; row += 1) {
    let allZero = true;
    for (let col = 0; col < varCount; col += 1) {
      if (rref[row]![col]!.num !== 0) {
        allZero = false;
        break;
      }
    }
    if (allZero && rref[row]![varCount]!.num !== 0) {
      return { kind: 'inconsistent' };
    }
  }

  // Count pivots
  const pivotCount = pivots.filter((p) => p !== null).length;

  // Unique solution: pivots == number of variables
  if (pivotCount === varCount) {
    const solutions = new Map<string, Expr>();
    for (let row = 0; row < equationCount; row += 1) {
      if (pivots[row] !== null) {
        const varIndex = pivots[row]!;
        const varName = vars[varIndex]!;
        const value = rref[row]![varCount]!;
        solutions.set(varName, num(value));
      }
    }
    return { kind: 'unique', solutions };
  }

  // Dependent: pivots < variables
  // Express pivot variables in terms of free variables
  const freeVars: string[] = [];
  for (let col = 0; col < varCount; col += 1) {
    const hasPivot = pivots.some((p) => p === col);
    if (!hasPivot) {
      freeVars.push(vars[col]!);
    }
  }

  // Build parameterisation: for each pivot var, express in terms of free vars
  const parameterisation = new Map<string, Expr>();

  for (let row = 0; row < equationCount; row += 1) {
    if (pivots[row] === null) continue;

    const pivotCol = pivots[row]!;
    const pivotVar = vars[pivotCol]!;

    // Equation: pivot_var + sum_free c_i * free_var_i = constant
    // => pivot_var = constant - sum_free c_i * free_var_i
    let expr: Expr = num(rref[row]![varCount]!);

    for (const freeVar of freeVars) {
      const freeCol = vars.indexOf(freeVar);
      const coeff = rref[row]![freeCol]!;
      if (coeff.num !== 0) {
        const term = mul(num(coeff.neg()), variable(freeVar));
        expr = { kind: 'add', children: [expr, term] };
      }
    }

    expr = canonicalise(expr);
    parameterisation.set(pivotVar, expr);
  }

  return { kind: 'dependent', parameterisation };
}

/**
 * 2x2 Substitution method
 */
function solveBySubstitution(
  equations: readonly Equation[],
  vars: readonly string[],
): SystemResult {
  if (equations.length !== 2 || vars.length !== 2) {
    throw new Error('substitution method requires exactly 2 equations and 2 variables');
  }

  const steps: SystemStep[] = [];
  const eq0 = equations[0]!;
  const eq1 = equations[1]!;

  const row0 = equationToMatrixRow(eq0, vars);
  const row1 = equationToMatrixRow(eq1, vars);
  if (row0 === null || row1 === null) {
    throw new Error('Equation is not linear');
  }

  // Pick the variable to isolate from eq0: prefer vars[0], else vars[1].
  const srcIdx = row0[0]!.num !== 0 ? 0 : row0[1]!.num !== 0 ? 1 : -1;

  if (srcIdx !== -1) {
    const otherIdx = 1 - srcIdx;
    const a = row0[srcIdx]!; // coeff of isolated var in eq0
    const bCo = row0[otherIdx]!; // coeff of the other var in eq0
    const cCo = row0[2]!; // rhs constant of eq0

    // Isolated: var_src = c/a − (b/a)·var_other
    const rhsTerms: Expr[] = [];
    if (bCo.num !== 0) {
      rhsTerms.push(mul(num(bCo.neg().div(a)), variable(vars[otherIdx]!)));
    }
    rhsTerms.push(num(cCo.div(a)));
    const isolatedRhs = canonicalise(
      rhsTerms.length === 1 ? rhsTerms[0]! : { kind: 'add', children: rhsTerms },
    );
    const isolatedEq: Equation = { lhs: variable(vars[srcIdx]!), rhs: isolatedRhs };
    steps.push({
      rule: 'substitute',
      from: vars[srcIdx]!,
      into: 0,
      result: isolatedEq,
      description: `Solve equation 1 for ${vars[srcIdx]!}`,
    });

    // Reduced: substitute into eq1 → (q − p·b/a)·var_other = r − p·c/a
    const p = row1[srcIdx]!;
    const q = row1[otherIdx]!;
    const r = row1[2]!;
    const reducedCoeff = q.sub(p.mul(bCo).div(a));
    const reducedConst = r.sub(p.mul(cCo).div(a));
    const reducedLhs =
      reducedCoeff.num === 0
        ? num(Fraction.ZERO)
        : canonicalise(mul(num(reducedCoeff), variable(vars[otherIdx]!)));
    const reducedEq: Equation = { lhs: reducedLhs, rhs: num(reducedConst) };
    steps.push({
      rule: 'substitute',
      from: vars[srcIdx]!,
      into: 1,
      result: reducedEq,
      description: `Substitute into equation 2`,
    });
  }

  const { rref, pivots } = gaussianRREF([Array.from(row0), Array.from(row1)], [], 2, 2);
  const solution = classifyRREF(rref, pivots, vars);
  return { solution, method: 'substitution', steps };
}

/**
 * Solve a system of linear equations.
 *
 * @param equations List of Equations (lhs = rhs)
 * @param vars Variable names in order
 * @param options Optional { method?: 'auto' | 'substitution' | 'gaussian' }
 * @returns SystemResult with solution and steps
 */
export function solveSystem(
  equations: readonly Equation[],
  vars: readonly string[],
  options?: { method?: 'auto' | 'substitution' | 'gaussian' },
): SystemResult {
  const method = options?.method ?? 'auto';

  // Auto method selection
  let selectedMethod: 'substitution' | 'gaussian';
  if (method === 'auto') {
    selectedMethod = equations.length === 2 && vars.length === 2 ? 'substitution' : 'gaussian';
  } else {
    selectedMethod = method;
  }

  // Dispatch to appropriate solver
  if (selectedMethod === 'substitution') {
    return solveBySubstitution(equations, vars);
  }

  // Gaussian elimination (default)
  const steps: SystemStep[] = [];

  // Convert equations to augmented matrix
  const matrix: Fraction[][] = [];
  for (const eq of equations) {
    const row = equationToMatrixRow(eq, vars);
    if (row === null) {
      throw new Error('Equation is not linear');
    }
    matrix.push(row);
  }

  // Run Gaussian elimination
  const { rref, pivots } = gaussianRREF(matrix, steps, equations.length, vars.length);

  // Classify and extract solution
  const solution = classifyRREF(rref, pivots, vars);

  return { solution, method: 'gaussian', steps };
}
